import { Link } from "react-router-dom";
import { Footer } from "@/components/Footer";
import { CTABanner } from "@/components/CTABanner";
import { Hero } from "@/components/Hero";
import { ProofStrip } from "@/components/ProofStrip";
import { SEO } from "@/components/SEO";
import { useState, useEffect, useRef } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import Lottie from "lottie-react";
import { homeCards } from "@/data/projects";
import { ViewportLottie } from "@/components/ViewportLottie";
import khaledProfile from "@/assets/khaled-profile.jpg";
import gallery1 from "@/assets/gallery-1.jpg";
import gallery2 from "@/assets/gallery-2.jpg";
import gallery3 from "@/assets/gallery-3.jpg";
import gallery4 from "@/assets/gallery-4.jpg";
import emptyTrashAnimation from "@/assets/empty-trash.json";

const projects = homeCards;

const Home = () => {
  const { t, language } = useLanguage();
  const [showAnimation, setShowAnimation] = useState(false);
  const [visibleProjects, setVisibleProjects] = useState<Set<number>>(new Set());
  const [visibleExperiences, setVisibleExperiences] = useState<Set<number>>(new Set());
  const projectRefs = useRef<(HTMLDivElement | null)[]>([]);
  const experienceRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowAnimation(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const observers = projectRefs.current.map((ref, index) => {
      if (!ref) return null;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setVisibleProjects((prev) => new Set(prev).add(index));
            }
          });
        },
        {
          threshold: 0.2,
          rootMargin: '0px 0px -100px 0px'
        }
      );

      observer.observe(ref);
      return observer;
    });

    return () => {
      observers.forEach((observer) => observer?.disconnect());
    };
  }, []);

  useEffect(() => {
    const observers = experienceRefs.current.map((ref, index) => {
      if (!ref) return null;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setVisibleExperiences((prev) => new Set(prev).add(index));
            }
          });
        },
        {
          threshold: 0.2,
          rootMargin: '0px 0px -100px 0px'
        }
      );

      observer.observe(ref);
      return observer;
    });

    return () => {
      observers.forEach((observer) => observer?.disconnect());
    };
  }, []);

  return (
    <div className="min-h-screen">
      <SEO
        title="Khaled Kasmou | Product & UX/UI Designer | Portfolio"
        description="Portfolio of Khaled Kasmou — product and UX/UI case studies spanning research-informed product thinking, interaction, interface craft, and design systems."
        path="/"
      />
      <main>
      {/* Hero Section with animations */}
      <Hero />

      {/* Proof strip — headline numbers */}
      <ProofStrip />


      {/* Projects Grid */}
      <section id="projects" className="max-w-[1300px] mx-auto px-6 pb-24 pt-12 relative">
        {/* Lottie Animation - Absolutely positioned */}
        {showAnimation && (
          <div aria-hidden="true" className="absolute top-24 left-1/2 -translate-x-1/2 z-0 w-64 h-64 md:w-80 md:h-80 animate-fade-in">
            <Lottie animationData={emptyTrashAnimation} loop={true} />
          </div>
        )}
        
        {/* Section Title */}
        <div className="relative z-10 mb-20">
          <h2 className="text-5xl md:text-7xl font-normal font-mono mb-4 animate-fade-in animation-delay-100 leading-[1.05] tracking-tight">
            <span className="bg-gradient-to-r from-foreground via-foreground to-muted-foreground bg-clip-text text-transparent">
              {t("home.projects.title")}
            </span>
          </h2>
          
          <p className="text-base text-muted-foreground font-mono max-w-2xl animate-fade-in animation-delay-200">
            {t("home.projects.subtitle")}
          </p>
        </div>
        
        
        {/* Featured Project - Large */}
        <div
          ref={(el) => (projectRefs.current[0] = el)}
          className={`transition-all duration-700 mb-16 ${
            visibleProjects.has(0)
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 translate-y-8'
          }`}
        >
          <Link
            to={`/project/${projects[0].caseStudyId}`}
            data-cursor-hover
            className="group block"
          >
            <div className="space-y-4">
              {/* Image */}
              <div className="w-full overflow-hidden bg-muted relative rounded-3xl transition-all duration-300 hover:shadow-[0_20px_60px_-15px_rgba(59,130,246,0.15)]">
                <img
                  src={projects[0].image}
                  alt={`${projects[0].title} — UX/UI case study by Khaled Kasmou`}
                  loading="lazy"
                  width="1200"
                  height="675"
                  className="w-full aspect-[4/3] sm:aspect-[16/9] lg:aspect-[21/9] object-cover transition-transform duration-700 group-hover:scale-105"
                />
                {/* Tag overlay */}
                <div className="absolute top-4 left-4">
                  <span className="text-xs px-3 py-1 bg-background/90 backdrop-blur-sm text-foreground rounded-full border border-border">
                    {projects[0].tags[0]}
                  </span>
                </div>
              </div>
              
              {/* Content */}
              <div className="space-y-2">
                <h3 className="text-2xl md:text-3xl font-normal text-foreground group-hover:text-primary transition-colors duration-300 leading-[1.05] tracking-tight">
                  {projects[0].title}
                </h3>
                
                {projects[0].descKey && (
                  <p className="text-base text-muted-foreground leading-relaxed">
                    {t(projects[0].descKey)}
                  </p>
                )}
              </div>
            </div>
          </Link>
        </div>

        {/* Smaller Projects Grid - 2x2 */}
        <div className="grid md:grid-cols-2 gap-16">
          {projects.slice(1).map((project, index) => {
            const actualIndex = index + 1;
            const hasCaseStudy = Boolean(project.caseStudyId);
            const cardInner = (
                  <div className="h-full flex flex-col space-y-3">
                    {/* Image */}
                    <div className="w-full overflow-hidden bg-muted relative rounded-3xl transition-all duration-300 hover:shadow-[0_20px_60px_-15px_rgba(59,130,246,0.15)]">
                      {project.animation ? (
                        <div className="w-full aspect-[4/3] relative" style={{ backgroundColor: project.animationBg ?? 'transparent' }}>
                          <ViewportLottie
                            animationData={project.animation}
                            className="w-full h-full"
                            preserveAspectRatio={project.animationFit === 'slice' ? 'xMidYMid slice' : 'xMidYMid meet'}
                          />

                          {/* Gradients only for Otis */}
                          {project.id === 'otis-ai-marketing' && (
                            <>
                              {/* Left fade */}
                              <div 
                                className="absolute left-0 top-0 h-full w-24 pointer-events-none"
                                style={{ background: 'linear-gradient(to right, #F2F1F3, transparent)' }}
                              />
                              {/* Right fade */}
                              <div 
                                className="absolute right-0 top-0 h-full w-24 pointer-events-none"
                                style={{ background: 'linear-gradient(to left, #F2F1F3, transparent)' }}
                              />
                            </>
                          )}
                        </div>
                      ) : (
                        <div className="w-full aspect-[4/3] relative">
                          <img
                            src={project.image}
                            alt={`${project.title} — ${project.company} project designed by Khaled Kasmou`}
                            loading="lazy"
                            width="600"
                            height="400"
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                          />
                        </div>
                      )}
                      {/* Tag overlay */}
                      <div className="absolute top-4 left-4 z-10">
                        <span className="text-xs px-3 py-1 bg-background/90 backdrop-blur-sm text-foreground rounded-full border border-border">
                          {project.tags[0]}
                        </span>
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div className="space-y-2 flex-1 flex flex-col">
                      <h3 className="text-xl font-normal text-foreground group-hover:text-primary transition-colors duration-300 leading-[1.05] tracking-tight">
                        {project.title}
                      </h3>
                      
                      {project.descKey && (
                        <p className="text-sm text-muted-foreground leading-relaxed flex-1">
                          {t(project.descKey)}
                        </p>
                      )}

                      {!hasCaseStudy && (
                        <span className="text-sm font-mono text-muted-foreground group-hover:text-foreground transition-colors">
                          {t("project.onRequest")} →
                        </span>
                      )}
                    </div>
                  </div>
            );
            return (
              <div
                key={project.id}
                ref={(el) => (projectRefs.current[actualIndex] = el)}
                className={`transition-all duration-700 ${
                  visibleProjects.has(actualIndex)
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-8'
                }`}
              >
                {hasCaseStudy ? (
                  <Link
                    to={`/project/${project.caseStudyId}`}
                    data-cursor-hover
                    className="group block h-full"
                  >
                    {cardInner}
                  </Link>
                ) : (
                  <Link to="/contact" data-cursor-hover className="group block h-full">
                    {cardInner}
                  </Link>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* About Me Section - Redesigned */}
      <section id="about" className="py-32 pb-16 px-6 bg-background">
        <div className="max-w-[1300px] mx-auto">
          {/* Section Title - Left aligned like My Work */}
          <div className="mb-20">
            <h2 className="text-5xl md:text-7xl font-normal font-mono mb-4 leading-[1.05] tracking-tight">
              <span className="bg-gradient-to-r from-foreground via-foreground to-muted-foreground bg-clip-text text-transparent">
                {t("home.about.title")}
              </span>
            </h2>
            
            <p className="text-base text-muted-foreground font-mono max-w-2xl">
              {t("home.about.subtitle")}
            </p>
          </div>

          {/* Content Grid */}
          <div className="grid md:grid-cols-2 gap-16">
            {/* Left Column - Image */}
            <div className="order-2 md:order-1">
              <div className="relative group h-full">
                <div className="rounded-2xl overflow-hidden h-full">
                  <img
                    src={khaledProfile}
                    alt="Khaled Kasmou — product and UX/UI designer based in Sofia, Bulgaria"
                    className="w-full h-full aspect-[4/5] md:aspect-auto md:min-h-[480px] object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-500"
                    loading="lazy"
                  />
                </div>
                {/* Decorative element */}
                <div className="absolute -bottom-6 -right-6 w-32 h-32 border-2 border-primary/20 rounded-2xl -z-10" />
              </div>
            </div>

            {/* Right Column - Content */}
            <div className="space-y-8 order-1 md:order-2 flex flex-col">

              <div className="space-y-6 text-base text-muted-foreground font-mono leading-relaxed">
                <p>{t("home.about.p1")}</p>

                <p>
                  {t("home.about.p2.before")} <a href="https://www.meetotis.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 font-medium hover:underline">OtisAI</a>{t("home.about.p2.middle")} <a href="https://www.kasha.io" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 font-medium hover:underline">Kasha.io</a>{t("home.about.p2.after")}
                </p>

                <p>{t("home.about.p3")}</p>

                <p>{t("home.about.p4")}</p>
              </div>

              {/* Experience Timeline */}
              <div className="space-y-4 pt-4 border-l-2 border-border/30 pl-6">
                <div 
                  ref={(el) => (experienceRefs.current[0] = el)}
                  className={`space-y-1 transition-all duration-700 ${
                    visibleExperiences.has(0)
                      ? 'opacity-100 translate-y-0'
                      : 'opacity-0 translate-y-4'
                  }`}
                  style={{ transitionDelay: '0ms' }}
                >
                  <div className="flex items-start justify-between gap-4">
                    <h3 className="font-semibold text-foreground">{language === "bg" ? "Ръководител Creative & UX/UI Design" : "Head of Creative & UX/UI Design"}</h3>
                    <span className="text-sm text-muted-foreground whitespace-nowrap">Sep 2024 - Oct 2025</span>
                  </div>
                  <p className="text-sm text-muted-foreground">Kasha.io</p>
                </div>
                
                <div 
                  ref={(el) => (experienceRefs.current[1] = el)}
                  className={`space-y-1 transition-all duration-700 ${
                    visibleExperiences.has(1)
                      ? 'opacity-100 translate-y-0'
                      : 'opacity-0 translate-y-4'
                  }`}
                  style={{ transitionDelay: '100ms' }}
                >
                  <div className="flex items-start justify-between gap-4">
                    <h3 className="font-semibold text-foreground">{language === "bg" ? "Ръководител UX/UI и бизнес анализатор" : "Head of UX/UI & Business Analyst"}</h3>
                    <span className="text-sm text-muted-foreground whitespace-nowrap">Sep 2023 - Sep 2025</span>
                  </div>
                  <p className="text-sm text-muted-foreground">Otis AI</p>
                </div>
                
                <div 
                  ref={(el) => (experienceRefs.current[2] = el)}
                  className={`space-y-1 transition-all duration-700 ${
                    visibleExperiences.has(2)
                      ? 'opacity-100 translate-y-0'
                      : 'opacity-0 translate-y-4'
                  }`}
                  style={{ transitionDelay: '200ms' }}
                >
                  <div className="flex items-start justify-between gap-4">
                    <h3 className="font-semibold text-foreground">Senior UX/UI Designer</h3>
                    <span className="text-sm text-muted-foreground whitespace-nowrap">Jun 2022 - Sep 2023</span>
                  </div>
                  <p className="text-sm text-muted-foreground">OpenTag</p>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-wrap gap-4 pt-4">
                <Link
                  to="/about"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-foreground text-background rounded-md font-mono text-sm font-medium hover:opacity-90 transition-opacity"
                >
                  {t("home.about.fullStory")}
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
                <Link
                  to="/cv"
                  className="focus-premium inline-flex items-center gap-2 px-6 py-3 border border-foreground/50 text-foreground rounded-md font-mono text-sm font-medium hover:border-foreground hover:bg-foreground/5 transition-all"
                >
                  {t("home.about.viewCV")}
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </Link>
                <a
                  href="/CV_Khaled_Kasmou.pdf"
                  download
                  className="focus-premium inline-flex items-center gap-2 px-6 py-3 border border-foreground/50 text-foreground rounded-md font-mono text-sm font-medium hover:border-foreground hover:bg-foreground/5 transition-all"
                >
                  {t("home.about.downloadCV")}
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Image Gallery Section */}
      <section className="py-32 bg-background relative">
        {/* Left fade overlay */}
        <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-background to-transparent z-10 pointer-events-none" />
        
        {/* Right fade overlay */}
        <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-background to-transparent z-10 pointer-events-none" />

        {/* Horizontal Scrolling Gallery */}
        <div className="overflow-x-auto scrollbar-hide">
          <div className="flex gap-4 sm:gap-6 pl-0 pr-0">
            {/* Image 1 - Always visible */}
            <div className="flex-shrink-0 w-[calc(50vw-16px)] sm:w-[calc(33.33vw-16px)] md:w-[calc(25vw-18px)] lg:w-[calc(20vw-19.2px)] h-[300px] sm:h-[400px] md:h-[450px] lg:h-[500px] rounded-r-2xl overflow-hidden group">
              <img
                src={gallery1}
                alt="Gallery image 1"
                className="w-full h-full object-cover object-center group-hover:scale-105 transition-all duration-500"
                loading="lazy"
              />
            </div>
            {/* Image 2 - Always visible */}
            <div className="flex-shrink-0 w-[calc(50vw-16px)] sm:w-[calc(33.33vw-16px)] md:w-[calc(25vw-18px)] lg:w-[calc(20vw-19.2px)] h-[300px] sm:h-[400px] md:h-[450px] lg:h-[500px] rounded-2xl overflow-hidden group">
              <img
                src={gallery2}
                alt="Gallery image 2"
                className="w-full h-full object-cover group-hover:scale-105 transition-all duration-500"
                loading="lazy"
              />
            </div>
            {/* Image 3 - Hidden on mobile, visible from sm up */}
            <div className="hidden sm:flex flex-shrink-0 w-[calc(33.33vw-16px)] md:w-[calc(25vw-18px)] lg:w-[calc(20vw-19.2px)] h-[400px] md:h-[450px] lg:h-[500px] rounded-2xl overflow-hidden group">
              <img
                src={gallery3}
                alt="Gallery image 3"
                className="w-full h-full object-cover group-hover:scale-105 transition-all duration-500"
                loading="lazy"
              />
            </div>
            {/* Image 4 - Hidden on mobile and sm, visible from md up */}
            <div className="hidden md:flex flex-shrink-0 w-[calc(25vw-18px)] lg:w-[calc(20vw-19.2px)] h-[450px] lg:h-[500px] rounded-2xl overflow-hidden group">
              <img
                src={gallery4}
                alt="Gallery image 4"
                className="w-full h-full object-cover group-hover:scale-105 transition-all duration-500"
                loading="lazy"
              />
            </div>
            {/* Image 5 - Hidden until lg (desktop) */}
            <div className="hidden lg:flex flex-shrink-0 w-[calc(20vw-19.2px)] h-[500px] rounded-l-2xl overflow-hidden group">
              <img
                src={khaledProfile}
                alt="Gallery image 5"
                className="w-full h-full object-cover group-hover:scale-105 transition-all duration-500"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      <CTABanner />
      </main>
      <Footer />

    </div>
  );
};

export default Home;
